import { loadDocument } from "~/.server/pdf";
import {
  Form,
  Link,
  redirect,
  useNavigation,
  useSubmit,
  data,
} from "react-router";
import { useEffect } from "react";
import { CONTENT_TYPE } from "~/libs/constants";
import { rateLimit } from "~/.server/rate-limiter";
import { fileStorage } from "~/.server/files";
import { plausible } from "~/.client/plausible";
import { logger } from "~/.server/logger";
// import { getSession } from "~/.server/supabase";
import api from "~/.server/api";
import { Route } from "./+types/route";

export async function action({ request }: Route.ActionArgs) {
  const formData = await request.formData();
  const docId = formData.get("key") as string;

  logger.info(
    {
      id: docId,
    },
    "process document",
  );

  const document = await loadDocument(docId);
  const totalPages = document?.countPages() || 0;

  let pageNumber = 0;
  let hasText = false;
  while (!hasText && pageNumber <= totalPages) {
    const page = await document?.loadPage(pageNumber);
    // Process the page data as needed
    const structuredText = page?.toStructuredText();
    hasText = structuredText!.asText().length > 0;
    pageNumber++;
  }

  if (!hasText) {
    return redirect(`/app?error=Text not found`);
  }

  logger.info(
    {
      page: pageNumber,
    },
    "nextPage",
  );

  const params = new URLSearchParams();
  params.set("book", docId);
  params.set("page", pageNumber.toString());

  return redirect(`/app/type?${params.toString()}`);
}

// const getHistory = async (session: string) => {
//   try {
//     const remoteHistory = await api.getHistory(session);
//     if (remoteHistory) {
//       const params = new URLSearchParams();
//       params.set("book", remoteHistory.book);
//       params.set("page", remoteHistory.page.toString());
//       return {
//         link: params.toString(),
//         preview_image: remoteHistory.preview_image,
//       };
//     }
//   } catch (e) {
//     logger.error(e, "getHistory");
//     return {};
//   }
// };

export async function loader({ request, context }: Route.LoaderArgs) {
  // const { session, headers } = await getSession(request);

  const url = new URL(request.url);
  const error = url.searchParams.get("error");

  const output: any = {
    error,
    blinkist: {
      link: "https://www.blinkist.com/",
      image:
        "https://www.blinkist.com/hubfs/brand-assets/Blinkist_Logo_White.png",
    },
  };

  // if (session) {
  //   output.history = await getHistory(session.access_token);
  // }

  logger.info(output.history, "loader history");

  try {
    await rateLimit(context);
    const { key, url } = await fileStorage.signedUrl();
    output.key = key;
    output.presignUrl = url;
  } catch (e) {
    logger.error(e);
  }

  return data(output, {
    // headers,
  });
}

export async function clientAction({
  request,
  serverAction,
}: Route.ClientActionArgs) {
  const clonedRequest = request.clone();
  const formData = await clonedRequest.formData();
  const file = formData.get("book") as File;
  const key = formData.get("key") as string;
  const presignUrl = formData.get("presignUrl") as string;

  if (file.size > 5 * 1024 * 1024) {
    return await redirect(`/app?error=File too large`);
  }

  try {
    await fetch(presignUrl!, {
      method: "PUT",
      body: file,
    });

    return serverAction();
  } catch (e) {
    console.error(e);
    return await redirect(`/app?error=Failed to process file`);
  }
}

export default function App({ loaderData }: Route.ComponentProps) {
  const { key, presignUrl, error, history, blinkist } = loaderData;
  const submit = useSubmit();

  // synchronize initially
  useEffect(() => {
    plausible("pageview", { props: { history: !!history } });
  }, []);

  const navigation = useNavigation();

  const faq = [
    {
      question: "What's the maximum file size?",
      answer: "The maximum file size that supported right now is up to 5MB",
    },
    {
      question: "How long does my file stored?",
      answer: "Right now, the file will be stored for 7 days",
    },
    {
      question: "What's the supported file type?",
      answer:
        "We currently only support PDF file format. EPUB support is now in beta",
    },
    {
      question: `Why I got an "Failed to process file" error?`,
      answer:
        "We process the file by parsing the text from the PDF file. If the file doesn't contain any text, or the text is on the image, you will get this error",
    },
  ];

  return (
    <div className="container mx-auto lg:h-full pt-4">
      {error && (
        <div className="alert alert-error">
          <div className="flex-1">
            <p>{error}</p>
          </div>
        </div>
      )}
      <div className="container mx-auto flex flex-col lg:h-full lg:object-center lg:items-center lg:flex lg:justify-center lg:pt-16">
        <div className="flex flex-col lg:flex-row">
          {history && (
            <div className="flex-none h-48 w-fit mx-auto">
              <div className="card bg-base-100 image-full h-full">
                <figure>
                  <img
                    src={`data:image/png;base64,${history.preview_image}`}
                    alt="Page Preview"
                    className="object-contain h-full"
                  />
                </figure>
                <div className="card-body">
                  <h2 className="card-title">Continue</h2>
                  <p>Pick up where you left</p>
                  <div className="card-actions justify-end">
                    <Link
                      to={`/app/type?${history.link}`}
                      className="btn btn-primary"
                    >
                      Page {history.page}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {history && (
            <div className="flex-auto divider lg:divider-horizontal">or</div>
          )}

          <div className="flex-auto basis-1/2 pl-16 pr-16 h-full flex flex-col">
            <h1 className="text-2xl font-bold">Read new book</h1>
            <div className="flex flex-col lg:flex-row lg:justify-center lg:items-center lg:mt-16">
              {blinkist && (
                <div className="flex-none h-48 w-fit mx-auto">
                  <div className="card bg-base-100 image-full h-full">
                    <figure>
                      <img
                        src={blinkist.image}
                        alt="Blinkist"
                        className="object-contain h-full"
                      />
                    </figure>
                    <div className="card-body">
                      <h2 className="card-title">Blinkist Daily</h2>
                      <p>
                        Get the key insights from the best non-fiction books
                      </p>
                      <p>Today's book is {blinkist.title}</p>
                      <div className="card-actions justify-end">
                        <a
                          href={blinkist.link}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-primary"
                        >
                          Read Now
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {blinkist && (
                <div className="flex-auto divider lg:divider-horizontal">
                  or
                </div>
              )}
              <Form
                method="post"
                encType="multipart/form-data"
                className="flex-none flex flex-col justify-center h-full"
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  formData.append("key", key!);
                  formData.append("presignUrl", presignUrl!);
                  submit(formData, {
                    method: "post",
                    encType: "multipart/form-data",
                  });
                }}
              >
                <p className="text-sm">
                  Upload a book (PDF or EPUB) to start reading
                </p>
                <label className="form-control w-full max-w-xs my-4">
                  <input
                    type="file"
                    className="file-input file-input-bordered w-full max-w-xs"
                    name="book"
                    accept={Object.values(CONTENT_TYPE).join(",")}
                    required
                  />
                </label>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    navigation.state === "submitting" && presignUrl !== null
                  }
                >
                  Upload
                </button>
              </Form>
            </div>
          </div>
        </div>
        <div className="flex-auto basis-1/2 p-16 h-full flex flex-col lg:mt-16">
          <h2 className="text-2xl font-bold mb-4">Frequently Asked Question</h2>
          {faq.map((item) => (
            <div
              className="collapse collapse-arrow bg-base-200"
              key={item.question}
            >
              <input type="radio" name="faq-accordion" />
              <div className="collapse-title text-xl font-medium">
                {item.question}
              </div>
              <div className="collapse-content">
                <p>{item.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
